import React from 'react';
import {
  Sparkles,
  TableProperties,
  CandlestickChart,
  BellRing,
  Bot,
  Star,
  NotebookPen,
  Gauge,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Feature {
  key:
    | 'scoring'
    | 'buyZone'
    | 'maSupport'
    | 'presets'
    | 'filters'
    | 'deepAnalysis'
    | 'alerts'
    | 'diary'
    | 'ai'
    | 'favorites';
  Icon: React.ElementType;
}

const FEATURES: Feature[] = [
  { key: 'scoring', Icon: Gauge },
  { key: 'buyZone', Icon: Target },
  { key: 'maSupport', Icon: TrendingUp },
  { key: 'presets', Icon: Sparkles },
  { key: 'filters', Icon: TableProperties },
  { key: 'deepAnalysis', Icon: CandlestickChart },
  { key: 'alerts', Icon: BellRing },
  { key: 'diary', Icon: NotebookPen },
  { key: 'ai', Icon: Bot },
  { key: 'favorites', Icon: Star },
];

const FeatureGrid: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section
      id="features"
      className="py-4 py-md-5"
      aria-labelledby="features-headline"
    >
      <div className="container">
        <div className="text-center mb-4">
          <h2
            id="features-headline"
            className="landing-section__h2 fw-semibold mb-2"
            style={{
              fontFamily: 'var(--font-display)',
              color: 'var(--text-primary)',
            }}
          >
            {t('landing.features.headline')}
          </h2>
          <p
            className="landing-section__sub mx-auto mb-0"
            style={{ color: 'var(--text-secondary)', maxWidth: 720 }}
          >
            {t('landing.features.sub')}
          </p>
        </div>

        <div className="row g-3" role="list">
          {FEATURES.map((feature, index) => {
            const Icon = feature.Icon;
            return (
              <div
                key={feature.key}
                role="listitem"
                className="col-12 col-md-6 col-lg-4"
              >
                <article
                  className="glass-card h-100 p-4 position-relative overflow-hidden"
                  data-testid={`feature-card-${feature.key}`}
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${index * 90}ms forwards`,
                    opacity: '0',
                  }}
                >
                  {/* Top accent line - replaces hover-only radial gradient */}
                  <div
                    className="position-absolute top-0 start-0 w-100"
                    aria-hidden
                    style={{
                      height: 2,
                      background:
                        'linear-gradient(90deg, transparent 0%, var(--accent-primary) 25%, var(--accent-primary) 75%, transparent 100%)',
                      opacity: 0.7,
                    }}
                  />

                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-2"
                      aria-hidden
                      style={{
                        width: 44,
                        height: 44,
                        backgroundColor: 'var(--accent-primary-muted)',
                        border: '1px solid rgba(242, 202, 80, 0.35)',
                        color: 'var(--accent-primary)',
                      }}
                    >
                      <Icon size={22} strokeWidth={1.75} />
                    </div>
                  </div>

                  <h3
                    className="landing-card__title fw-semibold mb-2"
                    style={{
                      fontFamily: 'var(--font-display)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {t(`landing.features.${feature.key}.title`)}
                    {feature.key === 'alerts' && (
                      <span className="ms-2 align-middle text-xs font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--accent-primary-muted)', color: 'var(--accent-primary)' }}>
                        {t('nav.beta', { defaultValue: 'Beta' })}
                      </span>
                    )}
                  </h3>

                  <p
                    className="landing-card__body mb-0"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {t(`landing.features.${feature.key}.body`)}
                  </p>
                </article>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeatureGrid;
